/**
 * UserMetricsInflation.ts
 *
 * Handles the inflation of V2 "grouped" S3 format back into flat UserMetricsRawData[].
 *
 * V2 format: Each S3 JSON row is an application-level header with an embedded `users` array.
 * This reduces S3 payload size by ~50-65% by deduplicating application-level fields that are
 * identical across all users within the same (application, month) group.
 *
 * The inflation step is transparent to the rest of the data processing pipeline — after
 * inflation, the data looks identical to the legacy flat format.
 *
 * Format detection is automatic: if raw[0] has a `users` key, it's V2 grouped format.
 */

import { UserMetricsRawData, UserMetricsUserRecord, UserMetricsGroupedRecord } from './UserMetricsInterfaces';

/**
 * Detects the S3 payload format and inflates grouped (V2) format to flat UserMetricsRawData[].
 *
 * - If the payload is already flat (V1 legacy), it's returned as-is with no performance cost.
 * - If the payload is grouped (V2), it's inflated by spreading header fields onto each user.
 *
 * Performance metrics are logged to console for measurement during rollout.
 *
 * @param raw - The raw S3 payload (either UserMetricsRawData[] or UserMetricsGroupedRecord[])
 * @returns UserMetricsRawData[] in the flat format expected by downstream processing
 */
export function inflateUserMetricsPayload(raw: unknown[]): UserMetricsRawData[] {
  if (!raw || raw.length === 0) return [];

  // Mixed-format support: the array may contain both V1 flat records and V2 grouped records
  // (e.g., when one data source has been migrated to V2 but others are still V1).
  // We process each record individually — grouped records are inflated, flat records pass through.
  const startTime = performance.now();
  const result: UserMetricsRawData[] = [];
  let v2GroupCount = 0;
  let v1FlatCount = 0;

  for (let i = 0; i < raw.length; i++) {
    const record = raw[i] as Record<string, unknown>;

    if ('users' in record && Array.isArray(record.users)) {
      // V2 grouped record — inflate to flat records
      v2GroupCount++;
      const grouped = record as unknown as UserMetricsGroupedRecord;
      const users = grouped.users;
      for (let j = 0; j < users.length; j++) {
        result.push({
          log_month: grouped.log_month,
          application: grouped.application,
          application_type: grouped.application_type,
          display_sub_application_name: grouped.display_sub_application_name,
          team_name: grouped.team_name,
          supervisor_login_name_hierarchy: grouped.supervisor_login_name_hierarchy,
          supervisor_name_hierarchy: grouped.supervisor_name_hierarchy,
          data_source: grouped.data_source,
          user_login: users[j].user_login,
          measure_value: users[j].measure_value,
          user_manager_login_hierarchy: users[j].user_manager_login_hierarchy,
          user_manager_name_hierarchy: users[j].user_manager_name_hierarchy,
          user_job_level: users[j].user_job_level,
          user_job_title: users[j].user_job_title,
          user_job_family: users[j].user_job_family,
          user_job_family_description: users[j].user_job_family_description,
          user_manager_job_level_hierarchy: users[j].user_manager_job_level_hierarchy,
          team_size_rollup: users[j].team_size_rollup,
          active_since: users[j].active_since,
          last_active: users[j].last_active
        } as UserMetricsRawData);
      }
    } else {
      // V1 flat record — pass through unchanged
      v1FlatCount++;
      result.push(record as unknown as UserMetricsRawData);
    }
  }

  const elapsed = performance.now() - startTime;
  if (v2GroupCount > 0) {
    console.log(
      `[UserMetrics] Mixed format inflated: ${v2GroupCount} V2 groups + ${v1FlatCount} V1 flat → ${result.length} total records in ${elapsed.toFixed(
        1
      )}ms`
    );
  } else {
    console.log(`[UserMetrics] V1 flat format: ${v1FlatCount} records (no inflation needed, ${elapsed.toFixed(1)}ms)`);
  }

  return result;
}
